import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Avatar,
} from "@mui/material";
import {
  ArrowForward as BackIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Straighten as MeasurementIcon,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getMeasurementDefinitionById,
  getMeasurementDefinitions,
  getHiddenMeasurementDefinitions,
  getMeasurementFormulaPreview,
  createMeasurementDefinition,
  updateMeasurementDefinition,
  deleteMeasurementDefinition,
  setMeasurementGenders,
} from "../services/MeasurementDefinitionService";
import { uploadImage } from "../services/MediaUploadService";
import { useMessageContext } from "../contexts/MessageContext";
import { useCompany } from "../contexts/CompanyContext";
import { RTL } from "../cache/CacheRtl";
import { t } from "../componenets/dashboard/coach/coachTokens";
import DeleteMeasurementDefinitionDialog from "../componenets/measurements/DeleteMeasurementDefinitionDialog";
import { UNIT_OPTIONS } from "../componenets/measurements/measurementUnits";
import TranslationDialogButton from "../componenets/translation/TranslationDialogButton";

/**
 * field key -> Hebrew label for the translation panel. The keys are the server's `@TField` keys for
 * MEASUREMENT_DEFINITION (`name`, `description`) — per FIELD, so enabling another locale needs no
 * entry here and no change anywhere on this page.
 */
const MEASUREMENT_FIELD_LABELS: Record<string, string> = {
  name: "שם המדד",
  description: "הוראות מדידה",
};

type Gender = "MALE" | "FEMALE";
type GenderValue = "BOTH" | Gender;

/** ["MALE","FEMALE"] -> "BOTH"; ["MALE"] -> "MALE"; etc. Defaults to BOTH. */
const gendersToValue = (genders?: Gender[] | null): GenderValue => {
  const set = new Set(genders || []);
  if (set.has("MALE") && set.has("FEMALE")) return "BOTH";
  if (set.has("MALE")) return "MALE";
  if (set.has("FEMALE")) return "FEMALE";
  return "BOTH";
};
const valueToGenders = (value: GenderValue): Gender[] =>
  (value === "BOTH" ? ["MALE", "FEMALE"] : [value]);

/** The editable form state. `gender` is the single-select projection of the def's gender rows. */
type FormState = {
  name: string;
  nameHeb: string;
  description: string;
  descriptionHeb: string;
  imageUrl: string;
  unit: string;
  displayOrder: number;
  gender: GenderValue;
  /** For a COMPUTED measurement only: which catalog measurements are its two inputs. "" = unmapped. */
  waistDefinitionId: string;
  neckDefinitionId: string;
};

/**
 * One option in a source picker.
 *
 * `hidden` options are rendered DISABLED rather than omitted. Omitting them is what caused the first
 * real misconfiguration: the company's צוואר measurement was hidden, so the only correct choice was
 * simply absent from the list, and חזה got picked instead. Showing it greyed out with "restore it
 * first" turns an invisible dead end into an instruction.
 */
type SourceOption = { id: number; label: string; hidden?: boolean };

/** What the current mapping produces across the company — the sanity check on a valid-but-wrong pick. */
type FormulaPreview = {
  traineesWithBothSources?: number;
  traineesWithValue?: number;
  traineesImplausible?: number;
  traineesMissingProfile?: number;
  sampleValue?: number | null;
};

/**
 * What נוסחת היקפים computes, spelled out for the admin configuring it. Shown as helper text under
 * the pickers, because "pick a waist measurement" is not self-evidently a body-fat calculation and
 * the two coefficients are the only way to tell which measurement belongs in which slot.
 */
const CIRCUMFERENCE_FORMULA_HELP =
  "אחוז שומן = 10.1 − (0.239 × גובה) + (0.8 × היקף בטן) − (0.5 × היקף צוואר) לגברים, " +
  "ועם 19.2 במקום 10.1 לנשים. הגובה והמגדר נלקחים מכרטיס המתאמן.";

/** Fields the form validates. Only the name is validated — the rest are optional or select-bound. */
type FieldName = "nameHeb";

/** A name shorter than this is almost certainly a typo, not a real measurement label. */
const MIN_NAME_LENGTH = 2;
/** Matches the column width on the server side; keeps a paste-accident from failing at the DB. */
const MAX_NAME_LENGTH = 60;

/**
 * The server rejects a duplicate active name with an English message. Map it to Hebrew so the user
 * never sees raw backend English — this is the backstop for the collisions the client-side check
 * can't see (e.g. a HIDDEN/soft-deleted definition, which the catalog list omits).
 */
const DUPLICATE_NAME_HEB = "מדד בשם זה כבר קיים";
const isDuplicateNameError = (message?: string | null) =>
  !!message && /already exists/i.test(message);

/** Canonical form for comparing names: case- and surrounding-whitespace-insensitive. */
const normalizeName = (value?: string | null) => (value || "").trim().toLowerCase();

/**
 * The services return `message?: string` — a successful call that has nothing to say omits it. The
 * notification handler wants a string, so give it one rather than letting an empty toast through.
 */
const toastText = (message: string | undefined, fallback: string) => message || fallback;

const GENDER_OPTIONS = [
  { value: "BOTH", label: "שני המגדרים" },
  { value: "MALE", label: "גברים בלבד" },
  { value: "FEMALE", label: "נשים בלבד" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: t.rs, backgroundColor: t.white, fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: t.border },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.green },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.green, borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { fontSize: "14px", color: t.t2, "&.Mui-focused": { color: t.green } },
};

const MeasurementDefinitionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Back to the catalog, on the tab it was opened from.
   *
   * The catalog's tab is component state, so navigating to the bare path always lands on כללי —
   * someone who opened a measurement from the גברים grid would be bounced somewhere else on save.
   * The originating tab rides along in router state (set by MeasurementDefinitionsScreen) and is
   * handed straight back; a deep link carries none and correctly falls back to כללי.
   */
  const goBackToCatalog = () =>
    navigate("/app/company-measurements", { state: { view: (location.state as any)?.from } });
  const notify = useMessageContext()?.notificationHandler;
  const { company } = useCompany();
  const isNew = id === "new";
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isSystem, setIsSystem] = useState(false); // seeded (legacyEnumType != null)
  /**
   * The server's `MeasurementFormula` when this is a COMPUTED measurement, else null. Never editable
   * here — a formula is created through its own endpoint, and what this page configures is only its
   * two inputs.
   */
  const [formula, setFormula] = useState<string | null>(null);
  const [sourceOptions, setSourceOptions] = useState<SourceOption[]>([]);
  const [preview, setPreview] = useState<FormulaPreview | null>(null);

  const [form, setForm] = useState<FormState>({
    name: "",
    nameHeb: "",
    description: "",
    descriptionHeb: "",
    imageUrl: "",
    unit: "cm",
    displayOrder: 0,
    gender: "BOTH", // BOTH | MALE | FEMALE
    waistDefinitionId: "",
    neckDefinitionId: "",
  });

  const isComputed = !!formula;

  /** `true` once the field has been blurred — errors stay hidden until the user leaves it. */
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});

  /**
   * Existing names in the company catalog, so a collision is reported as the user leaves the field
   * instead of after a failed submit. Excludes THIS definition on edit (renaming to your own current
   * name is not a collision). The catalog only lists ACTIVE definitions, so a hidden same-named def
   * still slips through — the server's rejection, mapped via DUPLICATE_NAME_HEB, is the backstop.
   */
  const [takenNames, setTakenNames] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    getMeasurementDefinitions(company?.id).then((res) => {
      if (cancelled || !res.success) return; // a failed fetch just means no client-side check
      const names = new Set<string>();
      const options: SourceOption[] = [];
      (res.data || []).forEach((d: any) => {
        const isSelf = !isNew && String(d.id) === String(id);
        if (!isSelf) {
          [d.name, d.nameHeb].forEach((n) => {
            const key = normalizeName(n);
            if (key) names.add(key);
          });
        }
        // Candidates for a formula's inputs. The catalog endpoint already returns only ACTIVE,
        // company-wide definitions (personal ones are excluded server-side), so the only extra
        // filtering needed is: not itself, and not another computed measurement.
        if (!isSelf && !d.computed && !d.formula && d.id != null) {
          options.push({ id: Number(d.id), label: d.nameHeb || d.name || `#${d.id}` });
        }
      });
      setTakenNames(names);
      setSourceOptions(options);
    });

    // Hidden measurements are listed in the pickers too — disabled, with a "restore it first" hint.
    // The server rejects a hidden source, so they can't be chosen; the point is that the admin can
    // SEE the measurement they are looking for exists, instead of picking a wrong one in its place.
    getHiddenMeasurementDefinitions(company?.id).then((res) => {
      if (cancelled || !res.success) return;
      const hiddenOptions = (res.data || [])
        .filter((d: any) => !d.computed && !d.formula && d.id != null)
        .map((d: any) => ({ id: Number(d.id), label: d.nameHeb || d.name || `#${d.id}`, hidden: true }));
      setSourceOptions((prev) => [...prev.filter((o) => !o.hidden), ...hiddenOptions]);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, company?.id]);

  useEffect(() => {
    if (!isNew) {
      getMeasurementDefinitionById(id, company?.id).then((res) => {
        if (res.success && res.data) {
          const d = res.data;
          setForm({
            name: d.name || "",
            nameHeb: d.nameHeb || "",
            description: d.description || "",
            descriptionHeb: d.descriptionHeb || "",
            imageUrl: d.imageUrl || "",
            unit: d.unit || "cm",
            displayOrder: d.displayOrder ?? 0,
            gender: gendersToValue(d.genders as Gender[] | undefined),
            waistDefinitionId: d.waistDefinitionId != null ? String(d.waistDefinitionId) : "",
            neckDefinitionId: d.neckDefinitionId != null ? String(d.neckDefinitionId) : "",
          });
          setIsSystem(!!d.legacyEnumType);
          setFormula(d.formula || null);
          if (d.formula) refreshPreview(id);
        } else {
          notify?.error("שגיאה בטעינת המדד");
          goBackToCatalog();
        }
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * Re-read the mapping health. Called on load and after each save, NOT on every picker change:
   * it reflects what is SAVED, and showing a verdict for a mapping that is not yet persisted would
   * be the wrong number next to the wrong state.
   */
  const refreshPreview = (definitionId?: string) => {
    const target = definitionId || id;
    if (isNew || !target) return;
    getMeasurementFormulaPreview(target).then((res) => {
      setPreview(res.success ? (res.data as FormulaPreview) : null);
    });
  };

  const handleChange = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  /**
   * A source picker shows a prompt when nothing is chosen rather than an empty box, so an unmapped
   * formula reads as "needs configuring" instead of looking like a rendering failure. `displayEmpty`
   * alone would render blank.
   */
  const renderSourceValue = (value: unknown) => {
    const selected = sourceOptions.find((o) => String(o.id) === String(value));
    if (selected) return selected.label;
    return <Box component="span" sx={{ color: t.t3 }}>בחרו מדידה…</Box>;
  };

  /**
   * Single source of truth for name validity — drives both the inline message and the save gate, so
   * what the user sees and what the button does can never disagree. The Hebrew name is the required
   * one (matching the field's `required` marker); English stays optional.
   *
   * `serverDuplicate` is set after a failed save so a collision the catalog couldn't see is shown
   * inline on the field rather than only as a toast.
   */
  const [serverDuplicate, setServerDuplicate] = useState<string | null>(null);

  const nameError = useMemo<string | undefined>(() => {
    const nameHeb = form.nameHeb.trim();
    if (!nameHeb) return "יש להזין שם מדד";
    if (nameHeb.length < MIN_NAME_LENGTH) return `שם המדד חייב להכיל לפחות ${MIN_NAME_LENGTH} תווים`;
    if (nameHeb.length > MAX_NAME_LENGTH) return `שם המדד ארוך מדי (עד ${MAX_NAME_LENGTH} תווים)`;
    if (takenNames.has(normalizeName(nameHeb))) return DUPLICATE_NAME_HEB;
    // A rejected name stays flagged until the user actually edits it (cleared in handleNameChange).
    if (serverDuplicate && normalizeName(serverDuplicate) === normalizeName(nameHeb)) {
      return DUPLICATE_NAME_HEB;
    }
    return undefined;
  }, [form.nameHeb, takenNames, serverDuplicate]);

  /** Editing a rejected name clears the server verdict — it no longer applies to the new value. */
  const handleNameChange = (field: "nameHeb" | "name") =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setServerDuplicate(null);
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const showNameError = touched.nameHeb ? nameError : undefined;

  /**
   * Disabled only while a save is in flight, or once the user can actually SEE why it's blocked.
   * On an untouched form the button stays clickable on purpose: a disabled button gives no feedback,
   * so clicking it is what reveals the inline message (see handleSave's early return). Submitting is
   * still impossible — handleSave re-checks and bails.
   */
  const isSaveDisabled = saving || !!showNameError;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const cdnUrl = await uploadImage(file);
      if (cdnUrl) {
        setForm((prev) => ({ ...prev, imageUrl: cdnUrl }));
        notify?.success("התמונה הועלתה בהצלחה");
      }
    } catch (error) {
      notify?.error("שגיאה בהעלאת התמונה");
    }
    setUploading(false);
  };

  const handleSave = async () => {
    // Reveal the inline message rather than firing a toast — the error belongs next to the field.
    if (nameError) {
      setTouched({ nameHeb: true });
      return;
    }
    setSaving(true);
    const genders = valueToGenders(form.gender);
    // `name` / `description` are the LEGACY English columns and are no longer user-editable —
    // English is an `entity_translation` row written through the translation panel. They are passed
    // through unchanged (and fall back to the Hebrew on create, where there is nothing to pass
    // through and `name` cannot be blank), so an installed mobile binary that sends no
    // Accept-Language keeps reading whatever English it read before. Phase 3 drops the columns.
    const data = {
      name: (form.name || form.nameHeb).trim(),
      nameHeb: form.nameHeb.trim(),
      description: form.description.trim(),
      descriptionHeb: form.descriptionHeb.trim(),
      imageUrl: form.imageUrl.trim(),
      unit: form.unit,
      displayOrder: form.displayOrder,
      ...(isNew ? { genders } : {}), // create honors genders; edit sets them separately below
      ...(company?.id ? { companyId: company.id } : {}),
      // Only a computed measurement has inputs, and the server ignores these on anything else. An
      // empty picker sends null, which CLEARS the mapping — that is how a locked input is released.
      ...(isComputed
        ? {
            waistDefinitionId: form.waistDefinitionId ? Number(form.waistDefinitionId) : null,
            neckDefinitionId: form.neckDefinitionId ? Number(form.neckDefinitionId) : null,
          }
        : {}),
    };

    const res = isNew
      ? await createMeasurementDefinition(data)
      : await updateMeasurementDefinition(id, data);

    if (res.success) {
      // The definition update endpoint doesn't touch gender membership — set it explicitly on edit.
      if (!isNew) {
        const gRes = await setMeasurementGenders(id, genders, company?.id);
        if (!gRes.success) {
          notify?.error(toastText(gRes.message, "שגיאה בעדכון המגדר של המדד"));
          setSaving(false);
          return;
        }
      }
      notify?.success(toastText(res.message, "המדד נשמר"));
      if (isComputed) refreshPreview();
      // Saving is the end of the task, for an edit as much as for a create — nobody comes to this
      // form to sit on it afterwards, and staying put made a successful save look like nothing had
      // happened. Both now return to the catalog, on the tab it was opened from.
      goBackToCatalog();
    } else if (isDuplicateNameError(res.message)) {
      // A collision the catalog couldn't see (e.g. a hidden definition). Flag it inline on the field
      // and in Hebrew, instead of surfacing the server's English text.
      setServerDuplicate(form.nameHeb.trim());
      setTouched({ nameHeb: true });
      notify?.error(DUPLICATE_NAME_HEB);
    } else {
      notify?.error(toastText(res.message, "שגיאה בשמירת המדד"));
    }
    setSaving(false);
  };

  const handleDelete = async (force: boolean) => {
    const res = await deleteMeasurementDefinition(id, force);
    if (res.success) {
      notify?.success(toastText(res.message, "המדד הוסתר"));
      goBackToCatalog();
    } else {
      notify?.error(toastText(res.message, "שגיאה במחיקת המדד"));
    }
    setDeleteOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress size={32} sx={{ color: t.green }} />
      </Box>
    );
  }

  const title = form.nameHeb || form.name;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <DeleteMeasurementDefinitionDialog
        open={deleteOpen}
        definition={{ id, name: title }}
        companyId={company?.id}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />

      {/* Back + title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, direction: "rtl" }}>
        <IconButton onClick={goBackToCatalog}
                    sx={{ color: t.t2, p: 1, "&:hover": { bgcolor: t.greenLight, color: t.green } }}>
          <BackIcon />
        </IconButton>
        <Typography sx={{ fontSize: { xs: "22px", md: "26px" }, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px" }}>
          {isNew ? "מדד חדש" : (title || "מדד")}
        </Typography>
      </Box>

      {/* Card */}
      <Box sx={{ background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh, overflow: "hidden", direction: "rtl" }}>
        {/* Header band */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: { xs: "14px 16px", md: "16px 22px" },
          borderBottom: `1px solid ${t.border}`,
          background: `linear-gradient(135deg, ${t.greenLight}, #f4fbf8)`,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "11px", background: t.white, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MeasurementIcon sx={{ color: t.green, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: t.text, letterSpacing: "-0.2px" }}>
                {isNew ? "מדד חדש" : "פרטי מדד"}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: t.t2 }}>
                {isComputed
                  ? "מדד מחושב — הערך מחושב אוטומטית ואינו ניתן להזנה"
                  : isNew ? "הוספת מדד גוף חדש" : (isSystem ? "מדד מערכת — ניתן לערוך תמונה, תיאור ויחידה" : "עריכת פרטי המדד")}
              </Typography>
            </Box>
          </Box>
          {!isNew && (
            <Box component="button" onClick={() => setDeleteOpen(true)}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "5px",
                background: t.white, color: t.red, height: 32, px: "12px", borderRadius: t.rs,
                fontSize: "12.5px", fontWeight: 600, border: `1px solid ${t.red}33`, cursor: "pointer", fontFamily: "inherit",
                transition: "all .14s", "&:hover": { background: t.red, color: "#fff", borderColor: t.red },
              }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
              מחק מדד
            </Box>
          )}
        </Box>

        {/* Body */}
        <RTL>
          <Box sx={{
            padding: { xs: "20px 16px", md: "28px 28px" },
            display: "grid",
            // A computed measurement has no image: the picture exists to show someone HOW to take the
            // measurement, and there is nothing to take.
            gridTemplateColumns: { xs: "1fr", md: isComputed ? "1fr" : "220px 1fr" },
            gap: { xs: 2.5, md: 4 }, alignItems: "start",
          }}>
            {/* Image column */}
            <Box sx={{ display: isComputed ? "none" : "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
              <Avatar src={form.imageUrl}
                sx={{ width: "100%", maxWidth: 180, height: 180, borderRadius: t.r, border: `1px solid ${t.border}`, bgcolor: t.greenLight, color: t.green }}
                variant="rounded">
                <MeasurementIcon sx={{ fontSize: 56 }} />
              </Avatar>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
              <Box component="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                sx={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", width: "100%", maxWidth: 180,
                  background: t.white, color: t.green, height: 36, px: "14px", borderRadius: t.rs,
                  fontSize: "13px", fontWeight: 600, border: `1px solid ${t.green}55`, cursor: uploading ? "default" : "pointer", fontFamily: "inherit",
                  transition: "all .14s", "&:hover": uploading ? {} : { background: t.greenLight }, "&:disabled": { opacity: 0.6 },
                }}>
                {uploading ? <CircularProgress size={15} sx={{ color: t.green }} /> : <UploadIcon sx={{ fontSize: 17 }} />}
                {uploading ? "מעלה..." : "העלאת תמונה"}
              </Box>
              <Typography sx={{ fontSize: 11.5, color: t.t3, textAlign: "center" }}>PNG / JPG · יחס מרובע מומלץ</Typography>
            </Box>

            {/* Fields column */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
              {/* ONE canonical name. Every other language — English included — is edited in the
                  translation panel, which renders itself from
                  GET /admin/translation/schema/MEASUREMENT_DEFINITION. */}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 2, alignItems: "start" }}>
                <TextField fullWidth label="שם המדד (עברית)" value={form.nameHeb}
                  onChange={handleNameChange("nameHeb")}
                  onBlur={() => setTouched((prev) => ({ ...prev, nameHeb: true }))}
                  error={!!showNameError}
                  helperText={showNameError || "שמות והוראות בשפות אחרות נערכים בחלון התרגומים"}
                  required sx={fieldSx} />
                <Box sx={{ pt: "9px" }}>
                  <TranslationDialogButton
                    type="MEASUREMENT_DEFINITION"
                    entityId={isNew ? null : Number(id)}
                    fieldLabels={MEASUREMENT_FIELD_LABELS}
                    entityNoun="המדד"
                    dialogTitle="תרגומי המדד"
                  />
                </Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                {/* A computed measurement's unit is fixed by what it computes — a percentage. */}
                <TextField select fullWidth label="יחידת מדידה" value={form.unit} onChange={handleChange("unit")}
                  disabled={isComputed}
                  helperText={isComputed ? "קבוע עבור מדד מחושב" : undefined} sx={fieldSx}>
                  {UNIT_OPTIONS.map((u) => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                </TextField>
                <TextField select fullWidth label="מיועד למגדר" value={form.gender} onChange={handleChange("gender")}
                  helperText="למי המדד יוצג — גברים, נשים או שניהם" sx={fieldSx}>
                  {GENDER_OPTIONS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                </TextField>
              </Box>

              {isComputed && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: t.text }}>
                    מדידות המקור
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    {/* InputLabelProps.shrink is required, not cosmetic: with `displayEmpty` the
                        select always renders something (the "בחרו מדידה…" placeholder), but MUI
                        still considers an empty value unfilled and leaves the floating label sitting
                        INSIDE the field — so label and placeholder paint on top of each other and
                        the field reads as blurred text. Pinning the label up fixes both pickers. */}
                    <TextField select fullWidth label="היקף בטן" value={form.waistDefinitionId}
                      onChange={handleChange("waistDefinitionId")}
                      inputProps={{ "aria-label": "היקף בטן" }}
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ displayEmpty: true, renderValue: renderSourceValue }}
                      sx={fieldSx}>
                      <MenuItem value="">
                        <em>ללא מדידה</em>
                      </MenuItem>
                      {/* The other picker's choice is excluded: the same measurement cannot be both. */}
                      {sourceOptions
                        .filter((o) => String(o.id) !== form.neckDefinitionId)
                        .map((o) => (
                          <MenuItem key={o.id} value={String(o.id)} disabled={o.hidden}>
                            {o.label}{o.hidden ? " — מוסתר, יש לשחזר תחילה" : ""}
                          </MenuItem>
                        ))}
                    </TextField>
                    <TextField select fullWidth label="היקף צוואר" value={form.neckDefinitionId}
                      onChange={handleChange("neckDefinitionId")}
                      inputProps={{ "aria-label": "היקף צוואר" }}
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ displayEmpty: true, renderValue: renderSourceValue }}
                      sx={fieldSx}>
                      <MenuItem value="">
                        <em>ללא מדידה</em>
                      </MenuItem>
                      {sourceOptions
                        .filter((o) => String(o.id) !== form.waistDefinitionId)
                        .map((o) => (
                          <MenuItem key={o.id} value={String(o.id)} disabled={o.hidden}>
                            {o.label}{o.hidden ? " — מוסתר, יש לשחזר תחילה" : ""}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: t.t2, lineHeight: 1.6 }}>
                    {CIRCUMFERENCE_FORMULA_HELP}
                  </Typography>
                  {/* Saying this up front is kinder than only refusing later, when they try. */}
                  <Typography sx={{ fontSize: 12, color: t.t3, lineHeight: 1.6 }}>
                    לא ניתן להסתיר או למחוק מדידה שנבחרה כאן — יש לנקות את הבחירה תחילה.
                  </Typography>
                  {!form.waistDefinitionId || !form.neckDefinitionId ? (
                    <Typography sx={{ fontSize: 12, color: t.t2, lineHeight: 1.6 }}>
                      עד שייבחרו שתי המדידות, המדד יוצג ללא ערך.
                    </Typography>
                  ) : null}

                  {/* What the SAVED mapping actually produces. A mapping can be perfectly valid and
                      still be the wrong two measurements — map חזה into the neck slot and every
                      trainee computes a negative percentage, which is dropped, which makes the
                      measurement vanish from every screen with nothing to explain it. This is the
                      one place that says so. */}
                  {preview && (preview.traineesWithBothSources || 0) + (preview.traineesMissingProfile || 0) > 0 && (
                    <Box sx={{
                      mt: 0.5, padding: "10px 12px", borderRadius: t.rs,
                      background: (preview.traineesWithValue || 0) === 0 ? t.redLight : t.greenLight,
                      border: `1px solid ${(preview.traineesWithValue || 0) === 0 ? `${t.red}33` : "rgba(1,187,167,.25)"}`,
                    }}>
                      {(preview.traineesWithValue || 0) === 0 ? (
                        <Typography sx={{ fontSize: 12.5, color: t.redText, lineHeight: 1.7, fontWeight: 600 }}>
                          אף אחד מ־{preview.traineesWithBothSources} המתאמנים עם שתי המדידות לא מקבל ערך תקין.
                          סביר שנבחרו המדידות הלא נכונות — ודאו שבחרתם היקף בטן והיקף צוואר.
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 12.5, color: t.text, lineHeight: 1.7 }}>
                          {preview.traineesWithValue} מתוך {preview.traineesWithBothSources} המתאמנים עם שתי המדידות
                          מקבלים ערך תקין
                          {preview.sampleValue != null ? ` (לדוגמה: ${preview.sampleValue}%)` : ""}.
                        </Typography>
                      )}
                      {(preview.traineesImplausible || 0) > 0 && (preview.traineesWithValue || 0) > 0 && (
                        <Typography sx={{ fontSize: 12, color: t.t2, lineHeight: 1.7, mt: 0.5 }}>
                          ל־{preview.traineesImplausible} מתאמנים התוצאה חורגת מהטווח הסביר ולא תוצג.
                        </Typography>
                      )}
                      {/* A missing height is a trainee-card problem, not a mapping problem — saying so
                          stops an admin re-picking measurements that were right all along. */}
                      {(preview.traineesMissingProfile || 0) > 0 && (
                        <Typography sx={{ fontSize: 12, color: t.t2, lineHeight: 1.7, mt: 0.5 }}>
                          ל־{preview.traineesMissingProfile} מתאמנים חסר גובה או מגדר בכרטיס, ולכן לא יוצג להם ערך.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              <TextField fullWidth label="הוראות מדידה (עברית)" value={form.descriptionHeb} onChange={handleChange("descriptionHeb")}
                multiline rows={4} placeholder={"כל שורה תופיע כפריט נפרד באפליקציה"} sx={fieldSx} />
            </Box>
          </Box>
        </RTL>

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", padding: { xs: "14px 16px", md: "16px 28px" }, borderTop: `1px solid ${t.border}`, background: t.bg }}>
          <Box component="button" onClick={handleSave} disabled={isSaveDisabled}
            sx={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
              background: t.green, color: "#fff", height: 40, px: "26px", borderRadius: t.rs,
              fontSize: "14px", fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
              "&:hover": { background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)" },
              "&:disabled": { background: t.border, color: t.t3, cursor: "not-allowed", transform: "none", boxShadow: "none" },
            }}>
            {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : (isNew ? "צור מדד" : "שמור שינויים")}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MeasurementDefinitionPage;
